import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";

const TICK_HOURS_INTERVAL = 4;
const SEGMENT_TOOLTIP_WIDTH = 190;
// Ignore micro-gaps that are too narrow to interact with reliably.
const MIN_INACTIVE_TOOLTIP_WIDTH_PX = 10;

function toLocalDayStart(value) {
  const day = dayjs(value);
  if (!day.isValid()) return null;
  return day.startOf("day").toDate();
}

function formatDuration(ms) {
  if (!ms || ms <= 0) return "0m";

  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const parts = [];

  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes || !parts.length) parts.push(`${minutes}m`);

  return parts.join(" ");
}

function formatGaugePercent(activeMs, totalMs) {
  if (!totalMs) return 0;
  return Math.round((activeMs / totalMs) * 100);
}

function formatLocalDateTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatRangeLabel(start, end) {
  const startLabel = formatLocalDateTime(start);
  const endLabel = formatLocalDateTime(end);
  return `${startLabel} - ${endLabel}`;
}

function formatOperationalSummary(totalActiveMs, startDay, endDay) {
  const start = dayjs(startDay);
  const end = dayjs(endDay);

  if (!start.isValid() || !end.isValid()) {
    return {
      lead: "Operational Summary:",
      detail: "",
    };
  }

  if (!totalActiveMs || totalActiveMs <= 0) {
    return {
      lead: "Operational Summary:",
      detail: "No activity detected for this period.",
    };
  }

  const activeLabel = formatDuration(totalActiveMs);
  if (start.isSame(end, "day")) {
    return {
      lead: "Operational Summary:",
      detail: `${activeLabel} active on ${start.format("DD MMM YYYY")}.`,
    };
  }

  const startLabel = start.year() === end.year() ? start.format("DD MMM") : start.format("DD MMM YYYY");
  return {
    lead: "Operational Summary:",
    detail: `${activeLabel} active from ${startLabel} - ${end.format("DD MMM YYYY")}.`,
  };
}

function formatDayHeader(value) {
  return value.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function buildRange(startDay, endDay) {
  const start = toLocalDayStart(startDay);
  const end = toLocalDayStart(endDay);
  if (!start || !end || start > end) return null;

  const now = new Date();
  const isToday = dayjs(endDay).isSame(dayjs(), "day");
  const naturalEnd = dayjs(end).add(1, "day").toDate();
  const clippedEnd = isToday ? new Date(Math.min(now.getTime(), naturalEnd.getTime())) : naturalEnd;

  return {
    start,
    end: clippedEnd,
  };
}

function buildTimeline(records, startDay, endDay) {
  const range = buildRange(startDay, endDay);
  if (!range) {
    return {
      range: null,
      activeSegments: [],
      inactiveSegments: [],
      totalActiveMs: 0,
      totalWindowMs: 0,
    };
  }

  const rawSegments = (records || []).reduce((acc, record) => {
    const status = String(record?.status || "").toUpperCase();
    if (status !== "ACTIVE") return acc;

    const rawStart = new Date(record.start);
    const rawEnd = new Date(record.end);
    if (Number.isNaN(rawStart.getTime()) || Number.isNaN(rawEnd.getTime())) return acc;

    const start = new Date(Math.max(rawStart.getTime(), range.start.getTime()));
    const end = new Date(Math.min(rawEnd.getTime(), range.end.getTime()));
    if (end <= start) return acc;

    acc.push({ start, end });
    return acc;
  }, []);

  const mergedSegments = rawSegments
    .sort((a, b) => a.start - b.start)
    .reduce((acc, current) => {
      const previous = acc[acc.length - 1];

      if (!previous) {
        acc.push({ ...current });
        return acc;
      }

      if (current.start.getTime() <= previous.end.getTime()) {
        previous.end = new Date(Math.max(previous.end.getTime(), current.end.getTime()));
        return acc;
      }

      acc.push({ ...current });
      return acc;
    }, []);

  let totalActiveMs = 0;
  const rangeMs = range.end.getTime() - range.start.getTime();
  const positionedSegments = mergedSegments.map((segment) => {
    const durationMs = segment.end.getTime() - segment.start.getTime();
    totalActiveMs += durationMs;

    return {
      ...segment,
      durationMs,
      leftPercent: ((segment.start.getTime() - range.start.getTime()) / rangeMs) * 100,
      widthPercent: (durationMs / rangeMs) * 100,
    };
  });

  const inactiveSegments = [];
  // Walk the timeline from range.start to range.end and collect every non-active gap.
  let cursor = new Date(range.start);

  positionedSegments.forEach((segment) => {
    if (segment.start > cursor) {
      const durationMs = segment.start.getTime() - cursor.getTime();
      inactiveSegments.push({
        start: new Date(cursor),
        end: new Date(segment.start),
        durationMs,
        leftPercent: ((cursor.getTime() - range.start.getTime()) / rangeMs) * 100,
        widthPercent: (durationMs / rangeMs) * 100,
      });
    }
    if (segment.end > cursor) cursor = new Date(segment.end);
  });

  if (cursor < range.end) {
    const durationMs = range.end.getTime() - cursor.getTime();
    inactiveSegments.push({
      start: new Date(cursor),
      end: new Date(range.end),
      durationMs,
      leftPercent: ((cursor.getTime() - range.start.getTime()) / rangeMs) * 100,
      widthPercent: (durationMs / rangeMs) * 100,
    });
  }

  return {
    range,
    activeSegments: positionedSegments,
    inactiveSegments,
    totalActiveMs,
    totalWindowMs: rangeMs,
  };
}

function buildTicks(range) {
  if (!range) return [];

  const ticks = [];

  for (
    let tick = new Date(range.start);
    tick <= range.end;
    tick = dayjs(tick).add(TICK_HOURS_INTERVAL, "hour").toDate()
  ) {
    const positionPercent =
      ((tick.getTime() - range.start.getTime()) / (range.end.getTime() - range.start.getTime())) * 100;

    ticks.push({
      key: tick.toISOString(),
      label: tick.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
      positionPercent,
    });
  }

  return ticks;
}

function buildDaySections(range) {
  if (!range) return [];

  const sections = [];
  const rangeMs = range.end.getTime() - range.start.getTime();

  for (
    let dayStart = dayjs(range.start).startOf("day");
    dayStart.toDate() < range.end;
    dayStart = dayStart.add(1, "day")
  ) {
    const sectionStart = new Date(Math.max(dayStart.toDate().getTime(), range.start.getTime()));
    const sectionEnd = new Date(
      Math.min(dayStart.add(1, "day").toDate().getTime(), range.end.getTime())
    );

    if (sectionEnd <= sectionStart) continue;

    const noon = dayStart.hour(12).minute(0).second(0).millisecond(0).toDate();
    const noonVisible = noon > sectionStart && noon < sectionEnd;

    sections.push({
      key: dayStart.toISOString(),
      label: formatDayHeader(dayStart.toDate()),
      leftPercent: ((sectionStart.getTime() - range.start.getTime()) / rangeMs) * 100,
      widthPercent: ((sectionEnd.getTime() - sectionStart.getTime()) / rangeMs) * 100,
      midnightPercent: ((sectionStart.getTime() - range.start.getTime()) / rangeMs) * 100,
      noonPercent: noonVisible ? ((noon.getTime() - range.start.getTime()) / rangeMs) * 100 : null,
    });
  }

  return sections;
}

const tooltipBaseStyle = {
  position: "absolute",
  zIndex: 4,
  minWidth: "176px",
  maxWidth: "220px",
  pointerEvents: "none",
  background: "#ffffff",
  border: "1px solid #e6ebf1",
  boxShadow: "0 16px 30px rgba(15, 23, 42, 0.14)",
  borderRadius: "12px",
  padding: "10px 12px",
};

const DeviceTimelineGraph = ({ data, startDay, endDay }) => {
  const [segmentTooltip, setSegmentTooltip] = useState(null);
  const [gaugeTooltip, setGaugeTooltip] = useState(false);
  const [timelineTrackWidth, setTimelineTrackWidth] = useState(0);
  const timelineTrackRef = useRef(null);

  const { range, activeSegments, inactiveSegments, totalActiveMs, totalWindowMs } = useMemo(
    () => buildTimeline(data, startDay, endDay),
    [data, startDay, endDay]
  );

  // Track rendered track width so percentage-based gaps can be thresholded in pixels.
  useEffect(() => {
    const trackNode = timelineTrackRef.current;
    if (!trackNode) return;

    const updateWidth = () => {
      setTimelineTrackWidth(trackNode.getBoundingClientRect().width);
    };

    updateWidth();

    // Fallback for environments without ResizeObserver support.
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }

    // Observe element-level resizes (not just window resize) to keep thresholding accurate.
    const observer = new ResizeObserver(updateWidth);
    observer.observe(trackNode);
    return () => observer.disconnect();
  }, [range]);

  const onlinePercent = formatGaugePercent(totalActiveMs, totalWindowMs);
  const offlineMs = Math.max(totalWindowMs - totalActiveMs, 0);
  const ticks = useMemo(() => buildTicks(range), [range]);
  const daySections = useMemo(() => buildDaySections(range), [range]);
  const isMultiDay = daySections.length > 1;
  const rangeDateLabel = range ? formatDayHeader(range.start) : "";
  const timelineCaption = useMemo(
    () => formatOperationalSummary(totalActiveMs, startDay, endDay),
    [totalActiveMs, startDay, endDay]
  );

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 250px",
        gap: "20px",
        alignItems: "start",
      }}
    >
      <div
        style={{
          position: "relative",
          background: "linear-gradient(180deg, #ffffff 0%, #fbfcfd 100%)",
          borderRadius: "24px",
          padding: "20px 20px 14px",
          border: "1px solid #ebeff4",
          boxShadow: "0 20px 42px rgba(15, 23, 42, 0.08)",
          minWidth: 0,
        }}
        >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "18px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0, fontSize: "12px", fontWeight: 500, color: "#374151" }}>
            <span style={{ width: "6px", height: "6px", borderRadius: "999px", background: "rgba(156, 163, 175, 0.5)", flex: "0 0 auto" }} />
            <span>
              <strong style={{ fontWeight: 700 }}>{timelineCaption.lead}</strong>{" "}
              {timelineCaption.detail}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", fontWeight: 700, color: "#344054" }}>
            <span style={{ width: "18px", height: "18px", borderRadius: "6px", background: "#22c55e" }} />
            ACTIVE
          </div>
        </div>

        {isMultiDay && (
          <div
            style={{
              position: "relative",
              height: "20px",
              marginBottom: "10px",
            }}
          >
            {daySections.map((section) => (
              <div
                key={`header-${section.key}`}
                style={{
                  position: "absolute",
                  left: `${section.leftPercent}%`,
                  width: `${section.widthPercent}%`,
                  textAlign: "center",
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#667085",
                  whiteSpace: "nowrap",
                }}
              >
                {section.label}
              </div>
            ))}
          </div>
        )}

        <div style={{ position: "relative" }}>
          <div
            ref={timelineTrackRef}
            style={{
              position: "relative",
              height: "34px",
              borderRadius: "999px",
              background: "linear-gradient(180deg, #e4e8ee 0%, #d6dbe3 100%)",
              boxShadow: "inset 0 2px 5px rgba(15, 23, 42, 0.08)",
              overflow: "hidden",
            }}
          >
            {isMultiDay
              ? daySections.map((section, index) => (
                  <React.Fragment key={`multi-skeleton-${section.key}`}>
                    {index > 0 && (
                      <div
                        style={{
                          position: "absolute",
                          left: `${section.midnightPercent}%`,
                          top: "0",
                          bottom: "0",
                          width: "2px",
                          background: "rgba(148, 163, 184, 0.42)",
                          transform: "translateX(-1px)",
                          zIndex: 1,
                        }}
                      />
                    )}
                    {section.noonPercent !== null && (
                      <div
                        style={{
                          position: "absolute",
                          left: `${section.noonPercent}%`,
                          top: "0",
                          bottom: "0",
                          width: "1px",
                          background: "rgba(255, 255, 255, 0.48)",
                          transform: "translateX(-0.5px)",
                          zIndex: 1,
                        }}
                      />
                    )}
                  </React.Fragment>
                ))
              : ticks.map((tick) => (
                  <div
                    key={`skeleton-${tick.key}`}
                    style={{
                      position: "absolute",
                      left: `${tick.positionPercent}%`,
                      top: "0",
                      bottom: "0",
                      width: "1px",
                      background: "rgba(255, 255, 255, 0.48)",
                      transform: "translateX(-0.5px)",
                      zIndex: 1,
                    }}
                  />
                ))}
            {activeSegments.map((segment, index) => (
              <div
                key={`${segment.start.toISOString()}-${index}`}
                onMouseEnter={(event) => {
                  const trackBounds = timelineTrackRef.current?.getBoundingClientRect();
                  if (!trackBounds) return;

                  const rawLeft = event.clientX - trackBounds.left;
                  const padding = SEGMENT_TOOLTIP_WIDTH / 2;
                  const maxLeft = Math.max(trackBounds.width - padding, padding);
                  const left = Math.min(Math.max(rawLeft, padding), maxLeft);

                  setSegmentTooltip({
                    left,
                    kind: "active",
                    start: segment.start,
                    end: segment.end,
                    durationMs: segment.durationMs,
                  });
                }}
                onMouseMove={(event) => {
                  const trackBounds = timelineTrackRef.current?.getBoundingClientRect();
                  if (!trackBounds) return;

                  const rawLeft = event.clientX - trackBounds.left;
                  const padding = SEGMENT_TOOLTIP_WIDTH / 2;
                  const maxLeft = Math.max(trackBounds.width - padding, padding);
                  const left = Math.min(Math.max(rawLeft, padding), maxLeft);

                  setSegmentTooltip({
                    left,
                    kind: "active",
                    start: segment.start,
                    end: segment.end,
                    durationMs: segment.durationMs,
                  });
                }}
                onMouseLeave={() => setSegmentTooltip(null)}
                style={{
                  position: "absolute",
                  top: "4px",
                  bottom: "4px",
                  left: `${segment.leftPercent}%`,
                  width: `${segment.widthPercent}%`,
                  minWidth: "2px",
                  borderRadius: "999px",
                  background: "linear-gradient(90deg, #34c759 0%, #149c3b 100%)",
                  boxSizing: "border-box",
                  borderLeft: "1px solid rgba(255, 255, 255, 0.72)",
                  borderRight: "1px solid rgba(7, 18, 34, 0.12)",
                  boxShadow: "0 8px 16px rgba(20, 156, 59, 0.28)",
                  cursor: "pointer",
                  zIndex: 2,
                }}
              />
            ))}
            {inactiveSegments.map((segment, index) => {
              // Convert this gap to px and allow hover only when it's visibly wide enough.
              const segmentPxWidth = (segment.widthPercent / 100) * timelineTrackWidth;
              const canShowTooltip = segmentPxWidth >= MIN_INACTIVE_TOOLTIP_WIDTH_PX;

              return (
                <div
                  key={`inactive-${segment.start.toISOString()}-${index}`}
                  onMouseEnter={(event) => {
                    if (!canShowTooltip) return;
                    const trackBounds = timelineTrackRef.current?.getBoundingClientRect();
                    if (!trackBounds) return;

                    const rawLeft = event.clientX - trackBounds.left;
                    const padding = SEGMENT_TOOLTIP_WIDTH / 2;
                    const maxLeft = Math.max(trackBounds.width - padding, padding);
                    const left = Math.min(Math.max(rawLeft, padding), maxLeft);

                    setSegmentTooltip({
                      left,
                      kind: "inactive",
                      start: segment.start,
                      end: segment.end,
                      durationMs: segment.durationMs,
                    });
                  }}
                  onMouseMove={(event) => {
                    if (!canShowTooltip) return;
                    const trackBounds = timelineTrackRef.current?.getBoundingClientRect();
                    if (!trackBounds) return;

                    const rawLeft = event.clientX - trackBounds.left;
                    const padding = SEGMENT_TOOLTIP_WIDTH / 2;
                    const maxLeft = Math.max(trackBounds.width - padding, padding);
                    const left = Math.min(Math.max(rawLeft, padding), maxLeft);

                    setSegmentTooltip({
                      left,
                      kind: "inactive",
                      start: segment.start,
                      end: segment.end,
                      durationMs: segment.durationMs,
                    });
                  }}
                  onMouseLeave={() => setSegmentTooltip(null)}
                  style={{
                    position: "absolute",
                    top: "4px",
                    bottom: "4px",
                    left: `${segment.leftPercent}%`,
                    width: `${segment.widthPercent}%`,
                    // Prevent hover hit-testing on tiny gaps to reduce tooltip noise.
                    pointerEvents: canShowTooltip ? "auto" : "none",
                    background: "transparent",
                    zIndex: 1,
                  }}
                />
              );
            })}
          </div>

          {segmentTooltip && (
            <div
              style={{
                ...tooltipBaseStyle,
                top: "calc(100% + 12px)",
                left: `${segmentTooltip.left}px`,
                transform: "translateX(-50%)",
              }}
            >
              {segmentTooltip.kind === "inactive" ? (
                <>
                  <div style={{ fontSize: "10px", fontWeight: 800, color: "#667085", letterSpacing: "0.04em" }}>
                    INACTIVE
                  </div>
                  <div style={{ fontSize: "11px", color: "#344054", marginTop: "6px", lineHeight: 1.4 }}>
                    <strong>From:</strong> {formatLocalDateTime(segmentTooltip.start)}
                  </div>
                  <div style={{ fontSize: "11px", color: "#344054", marginTop: "4px", lineHeight: 1.4 }}>
                    <strong>To:</strong> {formatLocalDateTime(segmentTooltip.end)}
                  </div>
                  <div style={{ fontSize: "11px", color: "#344054", marginTop: "4px", lineHeight: 1.4 }}>
                    <strong>Duration:</strong> {formatDuration(segmentTooltip.durationMs)}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: "10px", fontWeight: 800, color: "#149c3b", letterSpacing: "0.04em" }}>
                    ACTIVE
                  </div>
                  <div style={{ fontSize: "11px", color: "#344054", marginTop: "6px", lineHeight: 1.4 }}>
                    <strong>From:</strong> {formatLocalDateTime(segmentTooltip.start)}
                  </div>
                  <div style={{ fontSize: "11px", color: "#344054", marginTop: "4px", lineHeight: 1.4 }}>
                    <strong>To:</strong> {formatLocalDateTime(segmentTooltip.end)}
                  </div>
                  <div style={{ fontSize: "11px", color: "#344054", marginTop: "4px", lineHeight: 1.4 }}>
                    <strong>Duration:</strong> {formatDuration(segmentTooltip.durationMs)}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div
          style={{
            position: "relative",
            minHeight: isMultiDay ? "30px" : "42px",
            marginTop: "10px",
          }}
        >
          {isMultiDay ? (
            daySections.map((section) =>
              section.noonPercent === null ? null : (
                <div
                  key={`noon-label-${section.key}`}
                  style={{
                    position: "absolute",
                    left: `${section.noonPercent}%`,
                    bottom: "0",
                    transform: "translateX(-50%)",
                    fontSize: "11px",
                    color: "#667085",
                    whiteSpace: "nowrap",
                  }}
                >
                  12:00
                </div>
              )
            )
          ) : (
            <>
              <div
                style={{
                  position: "absolute",
                  left: "0",
                  top: "0",
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#667085",
                  whiteSpace: "nowrap",
                }}
              >
                {rangeDateLabel}
              </div>
              {ticks.map((tick) => (
                <React.Fragment key={tick.key}>
                  <div
                    style={{
                      position: "absolute",
                      left: `${tick.positionPercent}%`,
                      top: "0",
                      bottom: "16px",
                      width: "1px",
                      background: "#e6ebf1",
                      transform: "translateX(-0.5px)",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      left: `${tick.positionPercent}%`,
                      bottom: "0",
                      top: "18px",
                      transform: tick.positionPercent === 0 ? "translateX(0)" : tick.positionPercent === 100 ? "translateX(-100%)" : "translateX(-50%)",
                      fontSize: "11px",
                      color: "#667085",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {tick.label}
                  </div>
                </React.Fragment>
              ))}
            </>
          )}
        </div>
      </div>

      <div
        style={{
          position: "relative",
          background: "linear-gradient(180deg, #ffffff 0%, #f8fbf8 100%)",
          borderRadius: "22px",
          padding: "18px 16px",
          border: "1px solid #e6ece8",
          boxShadow: "0 18px 38px rgba(15, 23, 42, 0.08)",
        }}
      >
        <div
          onMouseEnter={() => setGaugeTooltip(true)}
          onMouseLeave={() => setGaugeTooltip(false)}
          style={{
            width: "158px",
            height: "158px",
            margin: "0 auto 14px",
            borderRadius: "50%",
            background: `conic-gradient(#31b14b ${onlinePercent}%, #d9dde4 0%)`,
            display: "grid",
            placeItems: "center",
            cursor: "default",
          }}
        >
          <div
            style={{
              width: "126px",
              height: "126px",
              borderRadius: "50%",
              background: "#fff",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "inset 0 0 0 1px #edf1ee",
            }}
          >
            <div style={{ fontSize: "20px", fontWeight: 800, color: "#101828", lineHeight: 1 }}>
              {onlinePercent}%
            </div>
            <div style={{ fontSize: "12px", fontWeight: 700, color: "#3f4b5b", marginTop: "6px" }}>
              online
            </div>
          </div>
        </div>

        <div style={{ textAlign: "center", fontSize: "14px", fontWeight: 700, color: "#17212f" }}>
          Availability Gauge
        </div>
        <div style={{ textAlign: "center", fontSize: "12px", color: "#667085", marginTop: "4px" }}>
          {range ? formatRangeLabel(range.start, new Date(range.end.getTime() - 1)) : "Selected range"}
        </div>

        {gaugeTooltip && (
          <div
            style={{
              ...tooltipBaseStyle,
              top: "18px",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            <div style={{ fontSize: "11px", fontWeight: 800, color: "#17212f", letterSpacing: "0.04em" }}>
              AVAILABILITY BREAKDOWN
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", marginTop: "10px", fontSize: "12px", color: "#344054" }}>
              <span>Active</span>
              <strong>{formatDuration(totalActiveMs)}</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", marginTop: "6px", fontSize: "12px", color: "#344054" }}>
              <span>Inactive</span>
              <strong>{formatDuration(offlineMs)}</strong>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceTimelineGraph;
